import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { isValidEmail, useValidators, getPhoneError as getPhoneErrorFor } from "@/lib/contactValidators";
import { useLanguage } from "@/context/LanguageContext";
import LocalePhoneInput from "@/components/LocalePhoneInput";
import { useContent } from "@/context/ContentContext";
import { useT } from "@/lib/translations";
import { trackFunnelStep } from "@/lib/analytics";
import {
  PawPrint,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Info,
  Heart,
  Home,
  Users,
  Clock,
  FileText,
  Send
} from "lucide-react";

const TOTAL_STEPS = 9;

export default function AdoptionFormPage() {
  const { dogId } = useParams();
  const navigate = useNavigate();
  const [dog, setDog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Adım değişiminde soru kartı doğrudan ekranın üstüne gelsin (ilk açılış hariç).
  const stepCardRef = useRef(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const el = stepCardRef.current;
    if (!el) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Yeni adımın içeriği yerleştikten sonra kaydır (scroll-mt-20 sticky nav boşluğunu bırakır).
    const id = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    return () => clearTimeout(id);
  }, [currentStep]);
  const [texts, setTexts] = useState({});
  const { t } = useContent();
  const L = useT();
  const { locale } = useLanguage();
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Önemli Bilgiler onayları
    onay_okudum: false,
    onay_randevusuz: false,
    onay_iade: false,
    onay_yogunluk: false,
    
    // Step 2: Hızlı Uygunluk
    sehir: "",
    ilce: "",
    semt: "",
    yas: "",
    yas_25_alti_aciklama: "",
    yas_25_alti_sorumluluk: "",
    gorusme_tercihi: "yuz_yuze",
    yurt_disindan_basvuru: false,
    referans_var: false,
    referans_bilgisi: "",
    iletisim_tercihi: "whatsapp",
    telefon: "",
    instagram_kullanici_adi: "",
    randevusuz_ziyaret_beklentisi: false,
    yalniz_kalma_saat: "",
    
    // Step 3: İletişim ve Konum
    ad_soyad: "",
    email: "",
    uygun_saat_araligi: "",
    
    // Step 4: Yaşam Koşulları
    ev_tipi: "",
    ev_durumu: "",
    ev_sahibi_izni: "",
    bina_yonetimi_kisiti: "",
    
    // Step 5: Ev Halkı
    evde_kisi_sayisi: "",
    evde_herkes_istiyor_mu: "",
    cocuk_var_mi: false,
    cocuk_yaslari: "",
    alerji_var_mi: "",
    
    // Step 6: Evde Başka Hayvan
    kedi_var_mi: false,
    kopek_var_mi: false,
    mevcut_hayvan_asi_kisir: "",
    tanistirma_plani: "",
    
    // Step 7: Günlük Hayat
    kim_ilgilenecek: "",
    tatilde_kim_bakacak: "",
    yuruyus_kac_kez: "",
    yuruyus_toplam_sure: "",
    kopek_nerede_yasayacak: "",
    
    // Step 8: Deneyim ve Motivasyon
    daha_once_kopek_bakti_mi: false,
    onceki_sahiplendirme_deneyimi: "",
    neden_sahiplenmek_istiyor: "",
    zorluk_deneyimi: "",
    
    // Step 9: Taahhütler
    taahut_randevusuz: false,
    taahut_ankara_disi: false,
    taahut_online: false,
    taahut_cip_devri: false,
    taahut_iade: false,
    taahut_yasal_ceza: false,
    taahut_takip: false
  });

  useEffect(() => {
    fetchDog();
    fetchTexts();
    // Funnel: adoption form opened (Meta InitiateCheckout equivalent)
    trackFunnelStep("begin_adoption", { dog_id: dogId, content_type: "adoption" });
  }, [dogId]);

  const fetchDog = async () => {
    try {
      const response = await axios.get(`${API}/dogs/${dogId}`);
      setDog(response.data.dog || response.data);
    } catch (error) {
      toast.error(L("Köpek bilgisi alınamadı"));
      navigate("/canlar");
    } finally {
      setLoading(false);
    }
  };

  const fetchTexts = async () => {
    try {
      const response = await axios.get(`${API}/pativista/configurable-texts`);
      setTexts(response.data.texts);
    } catch (error) {
      console.error("Texts fetch error:", error);
    }
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isNumericValue = (v) => /^\d+$/.test(String(v ?? "").trim());

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.onay_okudum && formData.onay_randevusuz && 
               formData.onay_iade && formData.onay_yogunluk;
      case 2:
        const yasValid = isNumericValue(formData.yas) && parseInt(formData.yas) > 0;
        const yas25Valid = parseInt(formData.yas) >= 25 || 
          (formData.yas_25_alti_aciklama && formData.yas_25_alti_sorumluluk);
        const iletisimValid = formData.iletisim_tercihi === "whatsapp" 
          ? !getPhoneErrorFor(formData.telefon, { required: true }, locale)
          : formData.instagram_kullanici_adi;
        const onlineValid = formData.gorusme_tercihi !== "online" || 
          formData.yurt_disindan_basvuru || formData.referans_var;
        return formData.sehir && formData.ilce && yasValid && yas25Valid && 
               iletisimValid && !formData.randevusuz_ziyaret_beklentisi && 
               isNumericValue(formData.yalniz_kalma_saat) && onlineValid;
      case 3:
        // Ad Soyad zorunlu, email girilmişse format doğru olmalı.
        if (!formData.ad_soyad) return false;
        if (formData.email && !isValidEmail(formData.email)) return false;
        return true;
      case 4:
        return formData.ev_tipi && formData.ev_durumu && formData.bina_yonetimi_kisiti &&
          (formData.ev_durumu !== "kira" || formData.ev_sahibi_izni);
      case 5:
        return isNumericValue(formData.evde_kisi_sayisi) && formData.evde_herkes_istiyor_mu && formData.alerji_var_mi;
      case 6:
        return true; // Optional fields
      case 7:
        return formData.kim_ilgilenecek && formData.tatilde_kim_bakacak && 
               isNumericValue(formData.yuruyus_kac_kez) && formData.yuruyus_toplam_sure && 
               formData.kopek_nerede_yasayacak;
      case 8:
        return formData.neden_sahiplenmek_istiyor && formData.onceki_sahiplendirme_deneyimi && formData.zorluk_deneyimi;
      case 9:
        const requiredTaahhutler = [
          formData.taahut_randevusuz,
          formData.taahut_cip_devri,
          formData.taahut_iade,
          formData.taahut_yasal_ceza,
          formData.taahut_takip
        ];
        // Conditional taahhütler
        if (formData.sehir.toLowerCase() !== "ankara") {
          requiredTaahhutler.push(formData.taahut_ankara_disi);
        }
        if (formData.gorusme_tercihi === "online") {
          requiredTaahhutler.push(formData.taahut_online);
        }
        return requiredTaahhutler.every(t => t);
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        dog_id: dogId,
        ...formData,
        yas: parseInt(formData.yas),
        yalniz_kalma_saat: parseInt(formData.yalniz_kalma_saat),
        evde_kisi_sayisi: parseInt(formData.evde_kisi_sayisi) || 1,
        yuruyus_kac_kez: parseInt(formData.yuruyus_kac_kez) || 1
      };
      
      const response = await axios.post(`${API}/pativista/adoption-application`, payload);
      toast.success(L("Başvurunuz alındı!"));
      navigate(`/basvuru-tamamlandi/${response.data.application_id}`, {
        state: { dogId, dogName: dog?.name }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || L("Başvuru gönderilemedi"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const progress = (currentStep / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <nav className="glass-nav sticky top-0 z-50 border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <PawPrint className="h-7 w-7 text-primary" strokeWidth={1.5} />
              <span className="font-heading text-xl font-semibold">KAHU</span>
            </Link>
            <div className="text-sm text-muted-foreground">
              {t("adoption.form.title", "Sahiplendirme Başvurusu")}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        {/* Form intro (CMS controlled) */}
        {t("adoption.form.description", "") && (
          <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
            {t("adoption.form.description", "")}
          </p>
        )}

        {/* Dog Info Bar */}
        {dog && (
          <div className="flex items-center gap-4 mb-6 p-4 bg-card rounded-xl border border-border/50">
            {dog.photo_url ? (
              <img src={dog.photo_url} alt={dog.name} className="w-16 h-16 rounded-xl object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center">
                <PawPrint className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
            <div>
              <h2 className="font-heading text-xl">{L("{name} için Başvuru").replace("{name}", dog.name)}</h2>
              <p className="text-sm text-muted-foreground">{dog.breed || L("Karışık")}</p>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{L("Adım {n} / {total}").replace("{n}", currentStep).replace("{total}", TOTAL_STEPS)}</span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Content */}
        <Card ref={stepCardRef} className="scroll-mt-20 bg-card border border-border/50 shadow-lg" data-testid="adoption-form-card">
          <CardContent className="p-6 md:p-8">
            {/* Step 1: Önemli Bilgiler */}
            {currentStep === 1 && (
              <Step1OnemliBilgiler formData={formData} updateForm={updateForm} texts={texts} />
            )}

            {/* Step 2: Hızlı Uygunluk */}
            {currentStep === 2 && (
              <Step2HizliUygunluk formData={formData} updateForm={updateForm} texts={texts} />
            )}

            {/* Step 3: İletişim ve Konum */}
            {currentStep === 3 && (
              <Step3Iletisim formData={formData} updateForm={updateForm} />
            )}

            {/* Step 4: Yaşam Koşulları */}
            {currentStep === 4 && (
              <Step4YasamKosullari formData={formData} updateForm={updateForm} texts={texts} />
            )}

            {/* Step 5: Ev Halkı */}
            {currentStep === 5 && (
              <Step5EvHalki formData={formData} updateForm={updateForm} texts={texts} />
            )}

            {/* Step 6: Evde Başka Hayvan */}
            {currentStep === 6 && (
              <Step6BaskaHayvan formData={formData} updateForm={updateForm} texts={texts} />
            )}

            {/* Step 7: Günlük Hayat */}
            {currentStep === 7 && (
              <Step7GunlukHayat formData={formData} updateForm={updateForm} texts={texts} />
            )}

            {/* Step 8: Deneyim ve Motivasyon */}
            {currentStep === 8 && (
              <Step8Deneyim formData={formData} updateForm={updateForm} />
            )}

            {/* Step 9: Taahhütler */}
            {currentStep === 9 && (
              <Step9Taahutler formData={formData} updateForm={updateForm} texts={texts} />
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-border/50">
              <Button
                variant="outline"
                onClick={() => currentStep === 1 ? navigate(`/can/${dogId}`) : setCurrentStep(prev => prev - 1)}
                className="btn-pill flex-shrink-0"
                data-testid="form-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                {currentStep === 1 ? L("Vazgeç") : L("Geri")}
              </Button>

              {currentStep < TOTAL_STEPS ? (
                <Button
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  disabled={!canProceed()}
                  className="btn-pill bg-primary text-primary-foreground flex-1 min-w-0 sm:flex-none text-sm sm:text-base"
                  data-testid="form-next-btn"
                >
                  <span className="text-center leading-tight">{currentStep === 1 ? L("Başvuruya Geç") : currentStep === 2 ? L("Detaylı Başvuru") : L("Devam")}</span>
                  <ChevronRight className="w-4 h-4 ml-2 flex-shrink-0" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceed() || submitting}
                  className="btn-pill bg-primary text-primary-foreground flex-1 min-w-0 sm:flex-none text-sm sm:text-base"
                  data-testid="form-submit-btn"
                >
                  <span className="text-center leading-tight">{submitting ? L("Gönderiliyor...") : L("Başvuruyu Gönder")}</span>
                  <Send className="w-4 h-4 ml-2 flex-shrink-0" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Step 1: Önemli Bilgiler
function Step1OnemliBilgiler({ formData, updateForm, texts }) {
  const L = useT();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Info className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-2xl">{L("Başlamadan Önce: Önemli Bilgiler")}</h2>
          <p className="text-muted-foreground">{L("Lütfen dikkatlice okuyun")}</p>
        </div>
      </div>

      <div className="space-y-4 text-sm leading-relaxed">
        <div className="p-4 bg-muted/30 rounded-xl">
          <h3 className="font-medium mb-2">{L("Süreç nasıl işler?")}</h3>
          <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
            <li>{L("Sahiplendirme başvurusu doldurulur.")}</li>
            <li>{L("Başvurular değerlendirilir ve uygun görülen kişilerle WhatsApp / Instagram üzerinden iletişime geçilir.")}</li>
            <li>{L("Genellikle")} <strong>{L("yüz yüze tanışma")}</strong> {L("yapılır. Online görüşme çok nadir; sadece yurt dışı başvurularında veya güvenilir referans varsa mümkündür.")}</li>
            <li>{L("Uygun eşleşme olursa teslim süreci başlar.")}</li>
            <li><strong>{L("Çip devri hemen yapılmaz:")}</strong>{L("sahiplenme sonrası 15 gün beklenir. 15 gün sonunda çip devri başlatılır.")}</li>
          </ol>
        </div>

        <div className="p-4 bg-muted/30 rounded-xl">
          <h3 className="font-medium mb-2">{L("Öncelik ve şehir")}</h3>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>{L("Ankara dışından sahiplendirme mümkündür.")}</li>
            <li><strong>{L("Öncelik Ankara içidir.")}</strong>{L("Ankara dışı başvurular ikinci sırada değerlendirilir.")}</li>
          </ul>
        </div>

        <div className="p-4 bg-muted/30 rounded-xl">
          <h3 className="font-medium mb-2">{L("Ziyaret")}</h3>
          <p className="text-muted-foreground"><strong>{L("Randevusuz ziyaret kabul edilmez.")}</strong></p>
        </div>

        <div className="p-4 bg-muted/30 rounded-xl">
          <h3 className="font-medium mb-2">{L("Geri dönüş")}</h3>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>{L("Genellikle")} <strong>{L("24 saat")}</strong> {L("içinde dönüş yapılır.")}</li>
            <li>{L("Yoğun dönemlerde dönüş")}<strong>{L("1 haftaya")}</strong> {L("uzayabilir.")}</li>
          </ul>
        </div>

        <div className="p-4 bg-muted/30 rounded-xl">
          <h3 className="font-medium mb-2">{L("Takip")}</h3>
          <p className="text-muted-foreground">{L("Sahiplendirme sonrası")}<strong>{L("15 günde bir")}</strong>{L("fotoğraf/video rica edilir.")}</p>
        </div>

        <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
          <h3 className="font-medium mb-2">{L("Uyum dönemi normaldir (3 gün – 3 hafta – 3 ay)")}</h3>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>{L("İlk 3 gün:")}</strong>{L("Köpek stres/şaşkınlık yaşayabilir; iştahsızlık, çekingenlik ve huzursuzluk görülebilir.")}</li>
            <li><strong>{L("İlk 3 hafta:")}</strong>{L("Rutin oturmaya başlar; gerçek karakteri ve ihtiyaçları daha net görünür.")}</li>
            <li><strong>{L("İlk 3 ay:")}</strong>{L("Bağ kurma ve tam uyum gerçekleşir.")}</li>
          </ul>
          <p className="text-muted-foreground mt-2"><strong>{L("Bu süreçte destek veririz; acele karar vermeyin.")}</strong></p>
        </div>

        <div className="p-4 bg-muted/30 rounded-xl">
          <h3 className="font-medium mb-2">{L("İade kuralı")}</h3>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>{L("Bakamayacağınızı anlarsanız köpeği")}<strong>{L("başkasına devredemezsiniz")}</strong>.</li>
            <li>{L("Köpek Pativista'ya iade edilir; \"bakamazlarsa köpek bizim köpeğimiz, geri alıyoruz\" kuralı geçerlidir.")}</li>
          </ul>
        </div>

        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <h3 className="font-medium mb-2 text-amber-800">{L("Yasal sorumluluk notu")}</h3>
          <ul className="list-disc list-inside space-y-1 text-amber-700">
            <li>{L("Pativista ceza kesmez.")}</li>
            <li>{L("Devletin çipli köpeğin kaybı vb. durumlarda ceza uygulayabildiği bilgisi vardır (")}<strong>93.000 TL</strong>{L("seviyesi bilgisi; zamanla değişebilir).")}</li>
          </ul>
        </div>
      </div>

      <div className="space-y-4 pt-6 border-t border-border/50">
        <h3 className="font-medium">{L("Onay Kutuları (zorunlu)")}</h3>
        
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Checkbox
              id="onay_okudum"
              checked={formData.onay_okudum}
              onCheckedChange={(v) => updateForm("onay_okudum", v)}
              data-testid="onay-okudum"
            />
            <Label htmlFor="onay_okudum" className="text-sm leading-relaxed cursor-pointer">
              {L("Yukarıdaki bilgileri okudum ve anladım.")}
            </Label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="onay_randevusuz"
              checked={formData.onay_randevusuz}
              onCheckedChange={(v) => updateForm("onay_randevusuz", v)}
              data-testid="onay-randevusuz"
            />
            <Label htmlFor="onay_randevusuz" className="text-sm leading-relaxed cursor-pointer">
              {L("Randevusuz ziyaret kabul edilmediğini anladım.")}
            </Label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="onay_iade"
              checked={formData.onay_iade}
              onCheckedChange={(v) => updateForm("onay_iade", v)}
              data-testid="onay-iade"
            />
            <Label htmlFor="onay_iade" className="text-sm leading-relaxed cursor-pointer">
              {L("Bakamazsam köpeği başkasına devredemeyeceğimi ve Pativista'ya iade edeceğimi anladım.")}
            </Label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="onay_yogunluk"
              checked={formData.onay_yogunluk}
              onCheckedChange={(v) => updateForm("onay_yogunluk", v)}
              data-testid="onay-yogunluk"
            />
            <Label htmlFor="onay_yogunluk" className="text-sm leading-relaxed cursor-pointer">
              {L("Genellikle 24 saat içinde dönüş olduğunu; yoğunlukta dönüşün 1 haftaya uzayabileceğini anladım.")}
            </Label>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 2: Hızlı Uygunluk Kontrolü
function Step2HizliUygunluk({ formData, updateForm, texts }) {
  const L = useT();
  const { getPhoneError } = useValidators();
  const isAnkara = formData.sehir.toLowerCase() === "ankara";
  const isUnder25 = formData.yas && parseInt(formData.yas) < 25;
  const isOnline = formData.gorusme_tercihi === "online";
  const isOver8Hours = formData.yalniz_kalma_saat && parseInt(formData.yalniz_kalma_saat) >= 8;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <CheckCircle className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-2xl">{L("Hızlı Uygunluk Kontrolü")}</h2>
          <p className="text-muted-foreground">{L("6 kısa soru")}</p>
        </div>
      </div>

      {/* Soru 1: Şehir */}
      <div className="space-y-3">
        <Label className="text-base font-medium">{L("1. Şehir / İlçe / Semt")}<span className="text-destructive">*</span></Label>
        <div className="grid grid-cols-3 gap-3">
          <Input
            placeholder={L("Şehir")}
            value={formData.sehir}
            onChange={(e) => updateForm("sehir", e.target.value)}
          />
          <Input
            placeholder={L("İlçe")}
            value={formData.ilce}
            onChange={(e) => updateForm("ilce", e.target.value)}
          />
          <Input
            placeholder={L("Semt (opsiyonel)")}
            value={formData.semt}
            onChange={(e) => updateForm("semt", e.target.value)}
          />
        </div>
        {formData.sehir && !isAnkara && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              {L(texts.ankara_oncelik || "Ankara dışından sahiplendirme mümkündür; ancak öncelik Ankara içidir. Ankara dışı başvurular ikinci sırada değerlendirilir.")}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Soru 2: Yaş */}
      <div className="space-y-3">
        <Label className="text-base font-medium">{L("2. Yaşınız")}<span className="text-destructive">*</span></Label>
        <NumberField
          value={formData.yas}
          onChange={(v) => updateForm("yas", v)}
          placeholder={L("Yaşınızı girin")}
          className="max-w-[150px]"
          testid="adoption-yas"
        />
        {isUnder25 && (
          <div className="space-y-3 p-4 bg-muted/30 rounded-xl">
            <p className="text-sm text-muted-foreground">
              {L("25+ yaş tercihi vardır. Lütfen aşağıdaki soruları cevaplayın:")}
            </p>
            <div>
              <Label>{L("25+ önceliğine rağmen neden değerlendirilmek istiyorsunuz?")}<span className="text-destructive">*</span></Label>
              <Textarea
                value={formData.yas_25_alti_aciklama}
                onChange={(e) => updateForm("yas_25_alti_aciklama", e.target.value)}
                placeholder={L("Açıklamanız...")}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>{L("Sorumluluğu nasıl karşılayacaksınız? (zaman, bütçe, düzen)")}<span className="text-destructive">*</span></Label>
              <Textarea
                value={formData.yas_25_alti_sorumluluk}
                onChange={(e) => updateForm("yas_25_alti_sorumluluk", e.target.value)}
                placeholder={L("Açıklamanız...")}
                className="mt-1.5"
              />
            </div>
          </div>
        )}
      </div>

      {/* Soru 3: Görüşme tercihi */}
      <div className="space-y-3">
        <Label className="text-base font-medium">{L("3. Görüşme tercihiniz")}<span className="text-destructive">*</span></Label>
        <RadioGroup
          value={formData.gorusme_tercihi}
          onValueChange={(v) => updateForm("gorusme_tercihi", v)}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yuz_yuze" id="yuz_yuze" />
            <Label htmlFor="yuz_yuze">{L("Yüz yüze")}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="online" id="online" />
            <Label htmlFor="online">Online</Label>
          </div>
        </RadioGroup>
        
        {isOnline && (
          <div className="space-y-3 p-4 bg-muted/30 rounded-xl">
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                {L(texts.online_istisna || "Online görüşme istisnadır; sadece yurt dışı başvurularında veya güvenilir referans varsa mümkündür.")}
              </AlertDescription>
            </Alert>
            
            <p className="text-sm font-medium">{L("En az birini seçin:")}</p>
            
            <div className="flex items-center gap-3">
              <Checkbox
                id="yurt_disi"
                checked={formData.yurt_disindan_basvuru}
                onCheckedChange={(v) => updateForm("yurt_disindan_basvuru", v)}
              />
              <Label htmlFor="yurt_disi">{L("Yurt dışından başvuruyorum")}</Label>
            </div>
            
            <div className="flex items-center gap-3">
              <Checkbox
                id="referans"
                checked={formData.referans_var}
                onCheckedChange={(v) => updateForm("referans_var", v)}
              />
              <Label htmlFor="referans">{L("Güvenilir referansım var")}</Label>
            </div>
            
            {formData.referans_var && (
              <div>
                <Label>{L("Referans adı ve iletişim bilgisi")}<span className="text-destructive">*</span></Label>
                <Input
                  value={formData.referans_bilgisi}
                  onChange={(e) => updateForm("referans_bilgisi", e.target.value)}
                  placeholder="Ad Soyad, telefon veya sosyal medya"
                  className="mt-1.5"
                />
              </div>
            )}
            
            {!formData.yurt_disindan_basvuru && !formData.referans_var && (
              <Alert className="border-destructive/50 bg-destructive/10">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <AlertDescription className="text-destructive">
                  {L("Online görüşme için yurt dışı veya referans şartı gereklidir. Aksi halde yüz yüze seçeneğini tercih edin.")}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </div>

      {/* Soru 4: İletişim tercihi */}
      <div className="space-y-3">
        <Label className="text-base font-medium">{L("4. İletişim tercihiniz")}<span className="text-destructive">*</span></Label>
        <RadioGroup
          value={formData.iletisim_tercihi}
          onValueChange={(v) => updateForm("iletisim_tercihi", v)}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="whatsapp" id="whatsapp" />
            <Label htmlFor="whatsapp">WhatsApp</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="instagram" id="instagram" />
            <Label htmlFor="instagram">Instagram</Label>
          </div>
        </RadioGroup>
        
        {formData.iletisim_tercihi === "whatsapp" && (
          <div>
            <Label>{L("Telefon numaranız")}<span className="text-destructive">*</span></Label>
            <div className="mt-1.5 max-w-[280px]">
              <LocalePhoneInput
                value={formData.telefon}
                onChange={(v) => updateForm("telefon", v)}
                testid="adoption-phone"
                invalid={!!(formData.telefon && getPhoneError(formData.telefon, { required: true }))}
              />
            </div>
            {formData.telefon && getPhoneError(formData.telefon, { required: true }) && (
              <p className="text-xs text-destructive mt-1.5" data-testid="adoption-phone-error">
                {getPhoneError(formData.telefon, { required: true })}
              </p>
            )}
          </div>
        )}
        
        {formData.iletisim_tercihi === "instagram" && (
          <div>
            <Label>{L("Instagram kullanıcı adınız")}<span className="text-destructive">*</span></Label>
            <Input
              value={formData.instagram_kullanici_adi}
              onChange={(e) => updateForm("instagram_kullanici_adi", e.target.value)}
              placeholder="@kullaniciadi"
              className="mt-1.5 max-w-[200px]"
            />
          </div>
        )}
      </div>

      {/* Soru 5: Randevusuz ziyaret */}
      <div className="space-y-3">
        <Label className="text-base font-medium">{L("5. Yaşam alanına randevusuz gelebileceğinizi düşünüyor musunuz?")}<span className="text-destructive">*</span></Label>
        <RadioGroup
          value={formData.randevusuz_ziyaret_beklentisi ? "evet" : "hayir"}
          onValueChange={(v) => updateForm("randevusuz_ziyaret_beklentisi", v === "evet")}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="evet" id="randevusuz_evet" />
            <Label htmlFor="randevusuz_evet">{L("Evet")}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="hayir" id="randevusuz_hayir" />
            <Label htmlFor="randevusuz_hayir">{L("Hayır")}</Label>
          </div>
        </RadioGroup>
        
        {formData.randevusuz_ziyaret_beklentisi && (
          <Alert className="border-destructive/50 bg-destructive/10">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <AlertDescription className="text-destructive">
              <strong>{L("Randevusuz ziyaret kabul edilmez.")}</strong> {L("Devam etmek için \"Hayır\" seçeneğini işaretlemeniz gerekir.")}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Soru 6: Yalnız kalma */}
      <div className="space-y-3">
        <Label className="text-base font-medium">{L("6. Köpek günde kaç saat yalnız kalacak?")}<span className="text-destructive">*</span></Label>
        <NumberField
          value={formData.yalniz_kalma_saat}
          onChange={(v) => updateForm("yalniz_kalma_saat", v)}
          placeholder={L("Saat")}
          className="max-w-[100px]"
          testid="adoption-yalniz-saat"
        />
        
        {isOver8Hours && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              {L(texts.yalniz_kalma_uyari || "Bazı köpekler uzun süre yalnız kalmaya uyum sağlayamayabilir. Bu durum görüşmede netleştirilecektir.")}
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}

// Step 3: İletişim ve Konum
function Step3Iletisim({ formData, updateForm }) {
  const L = useT();
  const { getEmailError } = useValidators();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <FileText className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-2xl">{L("İletişim ve Konum")}</h2>
          <p className="text-muted-foreground">Ekran 3/8</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <Label>Ad Soyad <span className="text-destructive">*</span></Label>
          <Input
            value={formData.ad_soyad}
            onChange={(e) => updateForm("ad_soyad", e.target.value)}
            placeholder={L("Adınız ve soyadınız")}
            className="mt-1.5"
          />
        </div>

        <div>
          <Label>E-posta <span className="text-muted-foreground text-xs">{L("(opsiyonel — başvurun ilerledikçe sana mail göndereceğiz)")}</span></Label>
          <Input
            type="email"
            value={formData.email || ""}
            onChange={(e) => updateForm("email", e.target.value)}
            placeholder="ornek@email.com"
            className="mt-1.5"
            data-testid="adoption-email"
          />
          {getEmailError(formData.email, { required: false }) && (
            <p className="text-xs text-destructive mt-1.5" data-testid="adoption-email-error">
              {getEmailError(formData.email, { required: false })}
            </p>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>{L("Şehir")}</Label>
            <Input value={formData.sehir} disabled className="mt-1.5 bg-muted" />
          </div>
          <div>
            <Label>{L("İlçe")}</Label>
            <Input value={formData.ilce} disabled className="mt-1.5 bg-muted" />
          </div>
          <div>
          <Label>{L("Semt")}</Label>
            <Input value={formData.semt || "—"} disabled className="mt-1.5 bg-muted" />
          </div>
        </div>

        <div>
          <Label>{L("Uygun saat aralığı (opsiyonel)")}</Label>
          <Input
            value={formData.uygun_saat_araligi}
            onChange={(e) => updateForm("uygun_saat_araligi", e.target.value)}
            placeholder={L("Örn: Hafta içi 18:00-21:00, Hafta sonu her saat")}
            className="mt-1.5"
          />
        </div>

        <p className="text-sm text-muted-foreground">
          <Info className="w-4 h-4 inline mr-1" />
          {L("Tam adres bu aşamada istenmez. Görüşme aşamasında paylaşılacaktır.")}
        </p>
      </div>
    </div>
  );
}

// Step 4: Yaşam Koşulları
function Step4YasamKosullari({ formData, updateForm, texts }) {
  const L = useT();
  const isKira = formData.ev_durumu === "kira";
  const izinBelirsiz = formData.ev_sahibi_izni === "hayir" || formData.ev_sahibi_izni === "bilmiyorum";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Home className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-2xl">{L("Yaşam Koşulları")}</h2>
          <p className="text-muted-foreground">{L("Ekran {n}/8").replace("{n}", "4")}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <Label>{L("Ev tipi")} <span className="text-destructive">*</span></Label>
          <Select value={formData.ev_tipi} onValueChange={(v) => updateForm("ev_tipi", v)}>
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder={L("Seçin")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="apartman">{L("Apartman")}</SelectItem>
              <SelectItem value="mustakil">{L("Müstakil")}</SelectItem>
              <SelectItem value="bahceli">{L("Bahçeli")}</SelectItem>
              <SelectItem value="diger">{L("Diğer")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>{L("Ev durumu")} <span className="text-destructive">*</span></Label>
          <Select value={formData.ev_durumu} onValueChange={(v) => updateForm("ev_durumu", v)}>
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder={L("Seçin")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="kendi">{L("Kendi evim")}</SelectItem>
              <SelectItem value="kira">{L("Kirada")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isKira && (
          <div>
            <Label>{L("Ev sahibi hayvana izin veriyor mu?")} <span className="text-destructive">*</span></Label>
            <Select value={formData.ev_sahibi_izni} onValueChange={(v) => updateForm("ev_sahibi_izni", v)}>
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder={L("Seçin")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="evet">{L("Evet")}</SelectItem>
                <SelectItem value="hayir">{L("Hayır")}</SelectItem>
                <SelectItem value="bilmiyorum">{L("Bilmiyorum")}</SelectItem>
              </SelectContent>
            </Select>
            
            {izinBelirsiz && (
              <Alert className="mt-3 border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  {L(texts.ev_izni_uyari || "İzin konusu görüşmede netleşir. İzin alamayacaksanız süreç ilerlemeyebilir.")}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <div>
          <Label>{L("Bina yönetimi kısıtı var mı?")}<span className="text-destructive">*</span></Label>
          <Select value={formData.bina_yonetimi_kisiti} onValueChange={(v) => updateForm("bina_yonetimi_kisiti", v)}>
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder={L("Seçin")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="evet">{L("Evet")}</SelectItem>
              <SelectItem value="hayir">{L("Hayır")}</SelectItem>
              <SelectItem value="bilmiyorum">{L("Bilmiyorum")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {(formData.bina_yonetimi_kisiti === "evet" || formData.bina_yonetimi_kisiti === "bilmiyorum") && (
          <Alert className="border-amber-200 bg-amber-50" data-testid="bina-yonetimi-warning">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700 text-sm">
              {L("Bazı apartman/site yönetim planlarında evcil hayvanla ilgili kısıtlar bulunabiliyor. Onay aşamasında yönetim planı veya bina yönetimi teyidi istenebilir. Kısıt varsa süreç ilerlemeyebilir.")}
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}

// Step 5: Ev Halkı
function Step5EvHalki({ formData, updateForm, texts }) {
  const L = useT();
  const uzlasiYok = formData.evde_herkes_istiyor_mu === "hayir" || formData.evde_herkes_istiyor_mu === "kararsiz";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Users className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-2xl">{L("Ev Halkı")}</h2>
          <p className="text-muted-foreground">{L("Ekran {n}/8").replace("{n}", "5")}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <Label>{L("Evde kaç kişi yaşıyor?")}<span className="text-destructive">*</span></Label>
          <NumberField
            value={formData.evde_kisi_sayisi}
            onChange={(v) => updateForm("evde_kisi_sayisi", v)}
            placeholder={L("Kişi sayısı")}
            className="mt-1.5 max-w-[100px]"
            testid="adoption-evde-kisi"
          />
        </div>

        <div>
          <Label>{L("Evde yaşayan herkes köpek istiyor mu?")}<span className="text-destructive">*</span></Label>
          <Select value={formData.evde_herkes_istiyor_mu} onValueChange={(v) => updateForm("evde_herkes_istiyor_mu", v)}>
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder={L("Seçin")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="evet">{L("Evet")}</SelectItem>
              <SelectItem value="hayir">{L("Hayır")}</SelectItem>
              <SelectItem value="kararsiz">{L("Kararsız")}</SelectItem>
            </SelectContent>
          </Select>
          
          {uzlasiYok && (
            <Alert className="mt-3 border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                {L(texts.ev_uzlasi_uyari || "Ev içi uzlaşı olmadan süreç ilerlemesi zorlaşabilir.")}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Checkbox
              id="cocuk_var"
              checked={formData.cocuk_var_mi}
              onCheckedChange={(v) => updateForm("cocuk_var_mi", v)}
            />
            <Label htmlFor="cocuk_var">{L("Evde çocuk var")}</Label>
          </div>
          
          {formData.cocuk_var_mi && (
            <div>
              <Label>{L("Çocukların yaşları")}</Label>
              <Input
                value={formData.cocuk_yaslari}
                onChange={(e) => updateForm("cocuk_yaslari", e.target.value)}
                placeholder={L("Örn: 5, 8, 12")}
                className="mt-1.5"
              />
            </div>
          )}
        </div>

        <div>
          <Label>{L("Evde hayvan alerjisi olan var mı?")}<span className="text-destructive">*</span></Label>
          <Select value={formData.alerji_var_mi} onValueChange={(v) => updateForm("alerji_var_mi", v)}>
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder={L("Seçin")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="evet">{L("Evet")}</SelectItem>
              <SelectItem value="hayir">{L("Hayır")}</SelectItem>
              <SelectItem value="bilmiyorum">{L("Bilmiyorum")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {(formData.alerji_var_mi === "evet" || formData.alerji_var_mi === "bilmiyorum") && (
          <Alert className="border-amber-200 bg-amber-50" data-testid="alerji-warning">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700 text-sm">
              {L("Alerji varsa ya da emin değilseniz, sağlığınız için onay aşamasında durumun netleşmesi istenebilir. Gerekirse alerji testi veya doktor görüşü talep edilebilir.")}
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}

// Step 6: Evde Başka Hayvan
function Step6BaskaHayvan({ formData, updateForm, texts }) {
  const L = useT();
  const hayvanVar = formData.kedi_var_mi || formData.kopek_var_mi;
  const saglikBelirsiz = formData.mevcut_hayvan_asi_kisir === "hayir" || formData.mevcut_hayvan_asi_kisir === "bilmiyorum";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <PawPrint className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-2xl">{L("Evde Başka Hayvan")}</h2>
          <p className="text-muted-foreground">{L("Ekran {n}/8").replace("{n}", "6")}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Checkbox
              id="kedi_var"
              checked={formData.kedi_var_mi}
              onCheckedChange={(v) => updateForm("kedi_var_mi", v)}
            />
            <Label htmlFor="kedi_var">{L("Evde kedi var")}</Label>
          </div>
          
          <div className="flex items-center gap-3">
            <Checkbox
              id="kopek_var"
              checked={formData.kopek_var_mi}
              onCheckedChange={(v) => updateForm("kopek_var_mi", v)}
            />
            <Label htmlFor="kopek_var">{L("Evde başka köpek var")}</Label>
          </div>
        </div>

        {hayvanVar && (
          <>
            <div>
              <Label>{L("Mevcut hayvanların aşı ve kısırlaştırma durumu")}</Label>
              <Select value={formData.mevcut_hayvan_asi_kisir} onValueChange={(v) => updateForm("mevcut_hayvan_asi_kisir", v)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder={L("Seçin")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="evet">{L("Aşılı ve kısır")}</SelectItem>
                  <SelectItem value="hayir">{L("Aşısız veya kısır değil")}</SelectItem>
                  <SelectItem value="bilmiyorum">{L("Bilmiyorum")}</SelectItem>
                </SelectContent>
              </Select>
              
              {saglikBelirsiz && (
                <Alert className="mt-3 border-amber-200 bg-amber-50">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700">
                    {L(texts.saglik_uyumu_uyari || "Sağlık uyumu için bu durum görüşmede netleşmelidir.")}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div>
              <Label>{L("Tanıştırma planınız nedir?")}</Label>
              <Textarea
                value={formData.tanistirma_plani}
                onChange={(e) => updateForm("tanistirma_plani", e.target.value)}
                placeholder={L("Yeni köpeği mevcut hayvanlarla nasıl tanıştırmayı planlıyorsunuz?")}
                className="mt-1.5"
              />
            </div>
          </>
        )}

        {!hayvanVar && (
          <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-xl">
            <Info className="w-4 h-4 inline mr-1" />
            {L("Evde başka hayvan yok. Bu alanları boş bırakabilirsiniz.")}
          </p>
        )}
      </div>
    </div>
  );
}

// Step 7: Günlük Hayat
function Step7GunlukHayat({ formData, updateForm, texts }) {
  const L = useT();
  const balkonBahce = formData.kopek_nerede_yasayacak === "balkon" || formData.kopek_nerede_yasayacak === "bahce";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Clock className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-2xl">{L("Günlük Hayat ve Bakım Planı")}</h2>
          <p className="text-muted-foreground">{L("Ekran {n}/8").replace("{n}", "7")}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <Label>{L("Kim ilgilenecek?")} <span className="text-destructive">*</span></Label>
          <Input
            value={formData.kim_ilgilenecek}
            onChange={(e) => updateForm("kim_ilgilenecek", e.target.value)}
            placeholder={L("Örn: Ben ve eşim")}
            className="mt-1.5"
          />
        </div>

        <div>
          <Label>{L("Tatilde kim bakacak?")} <span className="text-destructive">*</span></Label>
          <Input
            value={formData.tatilde_kim_bakacak}
            onChange={(e) => updateForm("tatilde_kim_bakacak", e.target.value)}
            placeholder={L("Örn: Ailem bakacak / Pet otel")}
            className="mt-1.5"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{L("Günde kaç kez yürüyüş?")}<span className="text-destructive">*</span></Label>
            <NumberField
              value={formData.yuruyus_kac_kez}
              onChange={(v) => updateForm("yuruyus_kac_kez", v)}
              placeholder={L("Sayı")}
              className="mt-1.5"
              testid="adoption-yuruyus-kez"
            />
          </div>
          <div>
            <Label>{L("Toplam yürüyüş süresi")}<span className="text-destructive">*</span></Label>
            <Input
              value={formData.yuruyus_toplam_sure}
              onChange={(e) => updateForm("yuruyus_toplam_sure", e.target.value)}
              placeholder={L("Örn: 1 saat")}
              className="mt-1.5"
            />
          </div>
        </div>

        <div>
          <Label>{L("Köpek evde nerede yaşayacak?")}<span className="text-destructive">*</span></Label>
          <Select value={formData.kopek_nerede_yasayacak} onValueChange={(v) => updateForm("kopek_nerede_yasayacak", v)}>
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder={L("Seçin")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ev_ici">{L("Ev içi")}</SelectItem>
              <SelectItem value="bahce">{L("Bahçe")}</SelectItem>
              <SelectItem value="balkon">{L("Balkon")}</SelectItem>
              <SelectItem value="diger">{L("Diğer")}</SelectItem>
            </SelectContent>
          </Select>
          
          {balkonBahce && (
            <Alert className="mt-3 border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                {L("Bu tercih görüşmede değerlendirilecektir. Bazı köpekler için ev içi yaşam daha uygun olabilir.")}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}

// Step 8: Deneyim ve Motivasyon
function Step8Deneyim({ formData, updateForm }) {
  const L = useT();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Heart className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-2xl">{L("Deneyim ve Motivasyon")}</h2>
          <p className="text-muted-foreground">{L("Ekran {n}/8").replace("{n}", "8")}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Checkbox
            id="onceki_deneyim"
            checked={formData.daha_once_kopek_bakti_mi}
            onCheckedChange={(v) => updateForm("daha_once_kopek_bakti_mi", v)}
          />
          <Label htmlFor="onceki_deneyim">{L("Daha önce köpek baktım")}</Label>
        </div>

        <div>
          <Label>{L("Daha önce sahiplendirme deneyiminiz var mı? Ne oldu?")}<span className="text-destructive">*</span></Label>
          <Textarea
            value={formData.onceki_sahiplendirme_deneyimi}
            onChange={(e) => updateForm("onceki_sahiplendirme_deneyimi", e.target.value)}
            placeholder={L("Varsa kısaca anlatın...")}
            className="mt-1.5"
          />
        </div>

        <div>
          <Label>{L("Neden sahiplenmek istiyorsunuz?")} <span className="text-destructive">*</span></Label>
          <Textarea
            value={formData.neden_sahiplenmek_istiyor}
            onChange={(e) => updateForm("neden_sahiplenmek_istiyor", e.target.value)}
            placeholder={L("Motivasyonunuzu açıklayın...")}
            className="mt-1.5"
            rows={4}
          />
        </div>

        <div>
          <Label>{L("Zorlandığınız bir durum oldu mu? Nasıl çözdünüz?")}<span className="text-destructive">*</span></Label>
          <Textarea
            value={formData.zorluk_deneyimi}
            onChange={(e) => updateForm("zorluk_deneyimi", e.target.value)}
            placeholder={L("Hayvan bakımında yaşadığınız zorluklar ve çözümler...")}
            className="mt-1.5"
          />
        </div>
      </div>
    </div>
  );
}

// Step 9: Taahhütler ve Onay
function Step9Taahutler({ formData, updateForm, texts }) {
  const L = useT();
  const isAnkaraDisi = formData.sehir.toLowerCase() !== "ankara";
  const isOnline = formData.gorusme_tercihi === "online";
  const binaKisiti = formData.bina_yonetimi_kisiti === "evet" || formData.bina_yonetimi_kisiti === "bilmiyorum";
  const alerjiVar = formData.alerji_var_mi === "evet" || formData.alerji_var_mi === "bilmiyorum";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <CheckCircle className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-2xl">{L("Taahhütler ve Onay")}</h2>
          <p className="text-muted-foreground">{L("Son adım")}</p>
        </div>
      </div>

      {/* Conditional info notes */}
      {(binaKisiti || alerjiVar) && (
        <div className="space-y-2 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="final-info-notes">
          <h4 className="text-sm font-medium text-amber-800">{L("Bilgilendirme")}</h4>
          {binaKisiti && (
            <p className="text-sm text-amber-700" data-testid="final-bina-note">
              {L("Not: Onay aşamasında bina yönetimi / yönetim planı teyidi istenebilir.")}
            </p>
          )}
          {alerjiVar && (
            <p className="text-sm text-amber-700" data-testid="final-alerji-note">
              {L("Not: Onay aşamasında gerekirse alerji testi veya doktor görüşü istenebilir.")}
            </p>
          )}
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        {L("Başvuruyu göndermeden önce aşağıdaki maddeleri onaylamanız gerekmektedir:")}
      </p>

      <div className="space-y-4 p-4 bg-muted/30 rounded-xl">
        <div className="flex items-start gap-3">
          <Checkbox
            id="taahut_randevusuz"
            checked={formData.taahut_randevusuz}
            onCheckedChange={(v) => updateForm("taahut_randevusuz", v)}
            data-testid="taahut-randevusuz"
          />
          <Label htmlFor="taahut_randevusuz" className="text-sm leading-relaxed cursor-pointer">
            {L("Randevusuz ziyaret kabul edilmediğini anladım.")}
          </Label>
        </div>

        {isAnkaraDisi && (
          <div className="flex items-start gap-3">
            <Checkbox
              id="taahut_ankara_disi"
              checked={formData.taahut_ankara_disi}
              onCheckedChange={(v) => updateForm("taahut_ankara_disi", v)}
              data-testid="taahut-ankara-disi"
            />
            <Label htmlFor="taahut_ankara_disi" className="text-sm leading-relaxed cursor-pointer">
              {L("Ankara dışındaysam önceliğin Ankara içi olduğunu anladım.")}
            </Label>
          </div>
        )}

        {isOnline && (
          <div className="flex items-start gap-3">
            <Checkbox
              id="taahut_online"
              checked={formData.taahut_online}
              onCheckedChange={(v) => updateForm("taahut_online", v)}
              data-testid="taahut-online"
            />
            <Label htmlFor="taahut_online" className="text-sm leading-relaxed cursor-pointer">
              {L("Online görüşmenin istisna olduğunu (yurt dışı başvurusu veya güvenilir referans), anladım.")}
            </Label>
          </div>
        )}

        <div className="flex items-start gap-3">
          <Checkbox
            id="taahut_cip_devri"
            checked={formData.taahut_cip_devri}
            onCheckedChange={(v) => updateForm("taahut_cip_devri", v)}
            data-testid="taahut-cip-devri"
          />
          <Label htmlFor="taahut_cip_devri" className="text-sm leading-relaxed cursor-pointer">
            {L("Sahiplenme sonrası 15 gün beklenip çip devrinin sonra başlatıldığını, anladım.")}
          </Label>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            id="taahut_iade"
            checked={formData.taahut_iade}
            onCheckedChange={(v) => updateForm("taahut_iade", v)}
            data-testid="taahut-iade"
          />
          <Label htmlFor="taahut_iade" className="text-sm leading-relaxed cursor-pointer">
            {L("Bakamazsam köpeği başkasına devredemeyeceğimi ve Pativista'ya iade edeceğimi anladım.")}
          </Label>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            id="taahut_yasal_ceza"
            checked={formData.taahut_yasal_ceza}
            onCheckedChange={(v) => updateForm("taahut_yasal_ceza", v)}
            data-testid="taahut-yasal-ceza"
          />
          <Label htmlFor="taahut_yasal_ceza" className="text-sm leading-relaxed cursor-pointer">
            {L("Pativista'nın ceza kesmediğini; ancak çipli hayvanın kaybı vb. durumlarda devletin cezaları olabileceğini (güncel bilgi 93.000 TL seviyesi; tutar değişebilir), anladım.")}
          </Label>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            id="taahut_takip"
            checked={formData.taahut_takip}
            onCheckedChange={(v) => updateForm("taahut_takip", v)}
            data-testid="taahut-takip"
          />
          <Label htmlFor="taahut_takip" className="text-sm leading-relaxed cursor-pointer">
            {L("Sahiplendirme sonrası 15 günde bir fotoğraf/video rica edileceğini, anladım.")}
          </Label>
        </div>
      </div>
    </div>
  );
}


// Sadece pozitif tam sayı kabul eden alan — hatalı girişte kırmızı çerçeve + uyarı.
function NumberField({ value, onChange, placeholder, className = "", testid, min = 1 }) {
  const L = useT();
  const raw = String(value ?? "").trim();
  const invalid = raw !== "" && (!/^\d+$/.test(raw) || parseInt(raw) < min);
  return (
    <div>
      <Input
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${className} ${invalid ? "border-destructive focus-visible:ring-destructive" : ""}`}
        data-testid={testid}
      />
      {invalid && (
        <p className="text-xs text-destructive mt-1.5" data-testid={`${testid}-error`}>
          {L("Sadece rakam girin (örn. 5)")}
        </p>
      )}
    </div>
  );
}
